import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import SEO from '../SEO';
import { portalApi } from '../../lib/portalApi';
import {
  CheckCircle2,
  ArrowRight,
  AlertCircle,
  Clock,
  ShieldAlert,
  RefreshCw,
  UserCheck,
} from 'lucide-react';

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [inviteData, setInviteData] = useState(null);
  const [valStatus, setValStatus] = useState('validating'); // 'validating' | 'valid' | 'revoked' | 'expired' | 'already_accepted' | 'not_found' | 'network_error'
  const [valMessage, setValMessage] = useState('');

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = useCallback(async () => {
    if (!token) {
      setValStatus('not_found');
      setValMessage('Missing invitation link or invalid URL parameter.');
      return;
    }
    setValStatus('validating');
    setValMessage('');
    try {
      const res = await portalApi.getInvite(token);
      if (res && res.valid) {
        setInviteData(res);
        setValStatus('valid');
      } else {
        setValStatus('not_found');
        setValMessage(res?.message || 'Invalid invitation.');
      }
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('revoked') || err.status === 410) {
        setValStatus('revoked');
        setValMessage('This invitation was revoked by an administrator.');
      } else if (msg.includes('already accepted') || msg.includes('already used') || err.status === 409) {
        setValStatus('already_accepted');
        setValMessage('This invitation has already been accepted and activated.');
      } else if (msg.includes('expired')) {
        setValStatus('expired');
        setValMessage('This invitation link has expired (links are valid for 7 days).');
      } else if (err.status === 404 || msg.includes('not found')) {
        setValStatus('not_found');
        setValMessage('Invitation link not found or invalid.');
      } else {
        setValStatus('network_error');
        setValMessage(msg || 'Unable to connect to the authentication server.');
      }
    }
  }, [token]);

  useEffect(() => {
    validate();
  }, [validate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await portalApi.acceptInvite(token, {
        fullName: fullName.trim() || undefined,
        password,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2200);
    } catch (err) {
      setSubmitError(err.message || 'Failed to activate account.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Accept Invitation" description="Accept your InnovHer staff invitation." />
      <section className="min-h-screen bg-[#08080a] flex items-center justify-center p-6 pt-24 pb-16">
        <div className="w-full max-w-md bg-white/[0.02] border border-white/10 p-8 rounded-3xl shadow-2xl backdrop-blur-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-[#d42b92]/15 rounded-full blur-[70px] pointer-events-none" />

          {/* 1. VALIDATING LOADER */}
          {valStatus === 'validating' && (
            <div className="text-center py-12 space-y-4">
              <RefreshCw className="w-8 h-8 animate-spin text-[#d42b92] mx-auto" />
              <p className="text-sm text-white/70 font-medium">Validating invitation credentials…</p>
              <p className="text-xs text-white/40">Connecting to InnovHer authentication gateway</p>
            </div>
          )}

          {/* 2. REVOKED STATE */}
          {valStatus === 'revoked' && (
            <div className="text-center py-6 space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center mx-auto">
                <ShieldAlert className="w-7 h-7" />
              </div>
              <h1 className="text-xl font-semibold text-white">Invitation Revoked</h1>
              <p className="text-xs text-white/60 leading-relaxed max-w-xs mx-auto">
                {valMessage || 'This invitation was revoked by an administrator and can no longer be used.'}
              </p>
              <div className="pt-2">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-medium transition-all"
                >
                  Return to Portal Login
                </Link>
              </div>
            </div>
          )}

          {/* 3. ALREADY ACCEPTED STATE */}
          {valStatus === 'already_accepted' && (
            <div className="text-center py-6 space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto">
                <UserCheck className="w-7 h-7" />
              </div>
              <h1 className="text-xl font-semibold text-white">Already Activated</h1>
              <p className="text-xs text-white/60 leading-relaxed max-w-xs mx-auto">
                This staff invitation has already been accepted and activated. You can sign in using your credentials.
              </p>
              <div className="pt-2">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#d42b92] hover:bg-[#b02279] text-white text-xs font-medium transition-all"
                >
                  Sign In to Dashboard <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          )}

          {/* 4. EXPIRED STATE */}
          {valStatus === 'expired' && (
            <div className="text-center py-6 space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center mx-auto">
                <Clock className="w-7 h-7" />
              </div>
              <h1 className="text-xl font-semibold text-white">Invitation Expired</h1>
              <p className="text-xs text-white/60 leading-relaxed max-w-xs mx-auto">
                Invitations are valid for 7 days. Please ask an administrator to send a new invitation link.
              </p>
              <div className="pt-2">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-medium transition-all"
                >
                  Return to Login
                </Link>
              </div>
            </div>
          )}

          {/* 5. NOT FOUND / INVALID STATE */}
          {valStatus === 'not_found' && (
            <div className="text-center py-6 space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center mx-auto">
                <AlertCircle className="w-7 h-7" />
              </div>
              <h1 className="text-xl font-semibold text-white">Invalid Invitation</h1>
              <p className="text-xs text-white/60 leading-relaxed max-w-xs mx-auto">
                {valMessage || 'This invitation link could not be found or has an invalid token.'}
              </p>
              <div className="pt-2">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-medium transition-all"
                >
                  Return to Login
                </Link>
              </div>
            </div>
          )}

          {/* 6. NETWORK ERROR STATE */}
          {valStatus === 'network_error' && (
            <div className="text-center py-6 space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center mx-auto">
                <AlertCircle className="w-7 h-7" />
              </div>
              <h1 className="text-xl font-semibold text-white">Connection Error</h1>
              <p className="text-xs text-white/60 leading-relaxed max-w-xs mx-auto">
                {valMessage || 'Failed to reach the API server. Please verify your connection.'}
              </p>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={validate}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#d42b92] hover:bg-[#b02279] text-white text-xs font-medium transition-all"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Retry Connection
                </button>
              </div>
            </div>
          )}

          {/* 7. VALID ACTIVATION FORM */}
          {valStatus === 'valid' && (
            <>
              <div className="text-center mb-6">
                <span className="text-xs uppercase tracking-wider text-[#d42b92] font-semibold">
                  {['founder', 'co_founder', 'startup'].includes(inviteData?.kind)
                    ? 'InnovHer Founder Workspace'
                    : 'InnovHer Staff Onboarding'}
                </span>
                <h1 className="text-2xl font-semibold text-white mt-2">Activate Your Account</h1>
                <p className="text-xs text-white/50 mt-1">
                  You are accepting an invitation as{' '}
                  <strong className="text-white uppercase font-mono">
                    {inviteData?.kind === 'founder'
                      ? 'Startup Founder'
                      : inviteData?.kind === 'co_founder'
                      ? 'Startup Co-Founder'
                      : inviteData?.kind}
                  </strong>
                  {(inviteData?.startupName || inviteData?.startupId?.name) ? (
                    <> for <strong className="text-[#ff8ec5]">{inviteData?.startupName || inviteData?.startupId?.name}</strong></>
                  ) : null}
                </p>
              </div>

              {/* READ-ONLY BOUND SERVER ATTRIBUTION */}
              <div className="mb-5 p-3 rounded-xl bg-black/50 border border-white/10 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-white/50">Invited Email:</span>
                  <span className="text-white font-medium font-mono">{inviteData?.email}</span>
                </div>
                {(inviteData?.startupName || inviteData?.startupId?.name) && (
                  <div className="flex items-center justify-between border-t border-white/5 pt-1.5">
                    <span className="text-white/50">Target Startup:</span>
                    <span className="text-[#ff8ec5] font-medium">{inviteData?.startupName || inviteData?.startupId?.name}</span>
                  </div>
                )}
              </div>

              {success ? (
                <div className="text-center space-y-3 py-6">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <h2 className="text-lg font-medium text-white">Account Created Successfully!</h2>
                  <p className="text-xs text-white/60">Redirecting to login…</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {submitError && (
                    <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                      {submitError}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-white/70 mb-1">Your Full Name</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Priya Sharma"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-white/70 mb-1">
                      Choose Password (min 8 characters)
                    </label>
                    <input
                      type="password"
                      required
                      minLength={8}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-white/70 mb-1">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      required
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92]"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full mt-2 py-3 rounded-xl bg-[#d42b92] hover:bg-[#b02279] text-white text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {submitting ? 'Creating Account…' : 'Activate Account'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}
